import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Shield } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { getApiUrl } from '../utils/api';

interface ServiceFormData {
  name: string;
  description: string;
  category: 'TATTOO' | 'PIERCING' | 'CONSULTATION' | 'AFTERCARE' | 'REMOVAL' | 'JEWELRY';
  subcategory?: string;
  basePrice: number;
  estimatedDuration: number;
  sessionCount?: number;
  isActive: boolean;
  onlineBookingEnabled: boolean;
  depositRequired: boolean;
  depositType?: 'FIXED' | 'PERCENTAGE';
  depositAmount?: number;
  maxAdvanceBookingDays?: number;
}

export function ServiceCreatePage() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<ServiceFormData>({
    name: '',
    description: '',
    category: 'TATTOO',
    subcategory: '',
    basePrice: 0,
    estimatedDuration: 30,
    sessionCount: undefined,
    isActive: true,
    onlineBookingEnabled: false,
    depositRequired: false,
    depositType: undefined,
    depositAmount: undefined,
    maxAdvanceBookingDays: undefined
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);

  const handleInputChange = (field: keyof ServiceFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.description || formData.basePrice <= 0 || formData.estimatedDuration <= 0) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);
      
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/services'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          priceRange: {},
          requirements: [],
          contraindications: [],
          aftercareInstructions: {},
          ageRestrictions: {},
          images: []
        }),
      });

      const data = await response.json();
      if (data.success) {
        navigate('/services');
      } else {
        setError(data.message || 'Échec de la création du service');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Create service error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/services')}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Créer un Service</span>
              <HelpIcon 
                docPath="services#creation" 
                tooltip="Documentation : Création d'un service"
              />
            </h1>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <Shield className="w-5 h-5 text-primary-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Informations Générales</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Nom du Service *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="input-field"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Catégorie *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="input-field"
                  required
                >
                  <option value="TATTOO">Tatouage</option>
                  <option value="PIERCING">Piercing</option>
                  <option value="CONSULTATION">Consultation</option>
                  <option value="AFTERCARE">Soins</option>
                  <option value="REMOVAL">Suppression</option>
                  <option value="JEWELRY">Bijouterie</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Sous-catégorie
                </label>
                <input
                  type="text"
                  value={formData.subcategory || ''}
                  onChange={(e) => handleInputChange('subcategory', e.target.value || undefined)}
                  className="input-field"
                  placeholder="Optionnel"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Pricing & Duration */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
              <Shield className="w-5 h-5 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Tarification & Durée</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Prix de base (€) *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.basePrice}
                onChange={(e) => handleInputChange('basePrice', parseFloat(e.target.value) || 0)}
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Durée estimée (min) *
              </label>
              <input
                type="number"
                min="1"
                value={formData.estimatedDuration}
                onChange={(e) => handleInputChange('estimatedDuration', parseInt(e.target.value) || 30)}
                className="input-field"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Nombre de sessions
              </label>
              <input
                type="number"
                min="1"
                value={formData.sessionCount || ''}
                onChange={(e) => handleInputChange('sessionCount', e.target.value ? parseInt(e.target.value) : undefined)}
                className="input-field"
                placeholder="Optionnel"
              />
            </div>
          </div>
        </div>

        {/* Online Booking Configuration */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-textPrimary">Configuration Réservation en Ligne</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.onlineBookingEnabled}
                  onChange={(e) => handleInputChange('onlineBookingEnabled', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="text-sm text-textPrimary">Réservation en ligne activée</span>
              </label>
            </div>

            <div>
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={formData.depositRequired}
                  onChange={(e) => handleInputChange('depositRequired', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="text-sm text-textPrimary">Acompte requis</span>
              </label>
            </div>

            {formData.depositRequired && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-7">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Type d'acompte
                  </label>
                  <select
                    value={formData.depositType || ''}
                    onChange={(e) => handleInputChange('depositType', e.target.value || undefined)}
                    className="input-field"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="FIXED">Montant fixe</option>
                    <option value="PERCENTAGE">Pourcentage</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">
                    Montant / Pourcentage
                  </label>
                  <input
                    type="number"
                    min="0"
                    step={formData.depositType === 'PERCENTAGE' ? '1' : '0.01'}
                    max={formData.depositType === 'PERCENTAGE' ? '100' : undefined}
                    value={formData.depositAmount || ''}
                    onChange={(e) => handleInputChange('depositAmount', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className="input-field"
                    placeholder={formData.depositType === 'PERCENTAGE' ? '0-100' : 'Montant en €'}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-textPrimary mb-2">
                Jours max. de réservation à l'avance
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxAdvanceBookingDays || ''}
                onChange={(e) => handleInputChange('maxAdvanceBookingDays', e.target.value ? parseInt(e.target.value) : undefined)}
                className="input-field"
                placeholder="Optionnel (ex: 90)"
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-6">
            <h2 className="text-xl font-semibold text-textPrimary">Statut</h2>
          </div>

          <div>
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="text-sm text-textPrimary">Service actif</span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-7">
              Les services inactifs n'apparaîtront pas dans les options de réservation
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/services')}
            className="btn-secondary"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            <span>{loading ? 'Création...' : 'Créer le Service'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}

